import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
import { Focusable } from "@xo-union/ui-accessibility";
import classesPropType from "@xo-union/classes-prop-type";
import { AnimatedElement } from "../Animation.js";
import LoadingContainer from "../LoadingContainer/index.js";
import CtaContainer from "../CtaContainer/index.js";
import { useLinkProps } from "../../utilities/index.js";

/**
 * Render a native link with union button styles
 *
 * ref is passed to underlying link
 */
const Link = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    as: Type,
    loadingText,
    ...ownProps
  } = _ref;
  const {
    isLoading
  } = ownProps;
  const finalProps = useLinkProps(ownProps, false);
  return /*#__PURE__*/React.createElement(Focusable, _extends({
    as: Type,
    ref: ref
  }, finalProps), /*#__PURE__*/React.createElement(AnimatedElement, {
    classes: ownProps.classes
  }), isLoading ? /*#__PURE__*/React.createElement(LoadingContainer, {
    classes: ownProps.classes,
    loadingText: loadingText
  }) : /*#__PURE__*/React.createElement(CtaContainer, _extends({
    classes: ownProps.classes
  }, ownProps)));
});
process.env.NODE_ENV !== "production" ? Link.propTypes = {
  /**
   * Disable link. Removes interactivity and hrefs
   */
  disabled: PropTypes.bool,
  /**
   * Link href. Removed if disabled
   */
  href: PropTypes.string,
  /**
   * Alternative prop to pass href to components that understand the `to` prop
   * (Such as `react-router`s `Link`)
   */
  to: PropTypes.string,
  /**
   * Component or element to render
   */
  as: PropTypes.elementType,
  /**
   * Breakpoint where button changes to block
   * */
  block: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', true]),
  /**
   * Class dependencies
   */
  classes: classesPropType(['animated-element', 'loading-container', 'loading-spinner']),
  /**
   * Color class
   * */
  color: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'quaternary', 'primary-alternative', 'secondary-alternative', 'tertiary-alternative', 'quaternary-alternative']),
  /**
   * Size class
   * */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /**
   * The button's content
   */
  children: PropTypes.node,
  /**
   * Click handler. Not required for links.
   */
  onClick: PropTypes.func,
  /**
   * Add additional classes
   */
  className: PropTypes.string,
  /**
   * Force active state
   */
  active: PropTypes.bool,
  /**
   * Text to display during the `isLoading` state
   */
  loadingText: PropTypes.string,
  /**
   * Loading state. Use this when the result of a button click is an async request
   */
  isLoading: PropTypes.bool
} : void 0;
Link.defaultProps = {
  as: 'a',
  size: 'lg',
  color: 'primary',
  isLoading: false,
  loadingText: 'Loading'
};
Link.displayName = 'Link';
export default Link;